import React from "react";
import DefaultProfile from "@/assets/graphics/default-profile.svg";
import { getUsersName } from "@/api/users/helpers";
import { ImageTypeHandler } from "@/app/(components)/Basic/ImageTypeHandler";
import { Nft } from "@/api/nfts/types";
import useMe from "@/shared/hooks/useMe";
import { extractDateAndTime, formatDateWithHours } from "@/shared/dates";
import { NftCommentsMorePopover } from "../../../Nft/NftCommentsMorePopover";
import { UserLink } from "../../../User/UserLink";
import { EndemicUserWrapper } from "@/app/(components)/Basic/EndemicUserWrapper";
import { ShortUser } from "@/api/users/types";

export interface Props {
  commentId?: string;
  author: ShortUser;
  createdAt: string | number;
  nft?: Nft;
  onDelete?: (id: string) => void;
  showDisplayName?: boolean;
}

export default function CommentAuthor({
  author,
  commentId,
  createdAt,
  nft,
  onDelete,
  showDisplayName,
}: Props) {
  const { me: user } = useMe();

  const { date, time } = extractDateAndTime(formatDateWithHours(createdAt));

  return (
    <div className="flex justify-between mb-2 max-w-full">
      <UserLink
        artist={author}
        tooltipPosition={["right", "middle"]}
        className="overflow-hidden w-full"
      >
        <div className="flex items-center gap-x-4 md:gap-x-3 w-full">
          <div className="relative w-8 h-8 md:w-10 md:h-10 shrink-0">
            {author.imageUrl ? (
              <ImageTypeHandler
                src={author.imageUrl}
                placeholder={author.imagePlaceholder}
                alt={getUsersName(author) || "profile image"}
                className="rounded-full"
                fill
              />
            ) : (
              <DefaultProfile />
            )}
          </div>
          <div className="flex md:flex-col overflow-hidden gap-1 w-full">
            <div className="flex md:gap-2 md:items-center flex-col md:flex-row">
              <EndemicUserWrapper id={author.id}>
                <div className="truncate">{getUsersName(author, true)}</div>
              </EndemicUserWrapper>
              {nft?.artist === author.address && (
                <span className="text-secondary text-body-4">Artist</span>
              )}
              {showDisplayName ? (
                <span className="text-endemicGray-700 md:text-base tracking-[-0.32px] endemic:text-primary endemic:opacity-60 text-sm">
                  @{author.displayName}
                </span>
              ) : (
                ""
              )}
            </div>
            <p className="md:text-body-4 text-endemicGray-700 endemic:text-secondary flex-col items-end mr-3 md:text-xs flex md:flex-row ml-auto text-xs md:m-0 md:gap-1">
              <span className="text-right">{date}</span>
              <span className="text-right">{time}</span>
            </p>
          </div>
        </div>
      </UserLink>
      <div>
        {user?.id === author.id && !!commentId && (
          <NftCommentsMorePopover id={commentId!} onDelete={onDelete!} />
        )}
      </div>
    </div>
  );
}
